import { useCallback, useEffect, useRef, useState } from 'react';

import type { DraftSaveStatus } from './useDraftSaver';

/**
 * Where the Equipment step's register writes stand, for the title bar's *Draft saved* tag
 * (`equipment-page-stage`, spec R15; epic question 6: *Draft saved* on this step means the
 * register's own save state).
 *
 * The step has no draft — every register write commits on its own — so this does not save
 * anything. It is told when a write begins and how it settled, and reports:
 *
 * - `saving` while any begun write is unsettled;
 * - else `failed` when the LAST write to settle failed (an earlier failure followed by a success
 *   is over: the tag says the latest truth, and the earlier write's own error stays where its
 *   action shows it);
 * - else `saved`, with `savedAt` the time of the last successful write this session (null before
 *   one, which the tag renders as *Draft saved* with no time).
 *
 * **Per twin (`scope`).** A failure, a saved time or an open write on one twin never shows on
 * another's tag: when the scope changes, all of it is dropped, and a late settle for a write begun
 * under the old scope is ignored.
 *
 * **Retry is a READ, never a repeat** (ruling 3, Rob 2026-10-13). An add, an upload or a correction
 * is not idempotent and its outcome may be unknown, so repeating it could write twice. Retry
 * re-reads the register so the page shows what actually landed, and clears the failure once that
 * read comes back `ready` — but only the failure it was pressed for: a write that settles while the
 * re-read runs decides the tag itself. While the re-read is in flight the status reads `saving`, so
 * the tag keeps its Retry button, busy, rather than dropping the operator's focus (this is the one
 * `saving` that is not a write; recorded in the review). Whatever re-read next lands — even one that
 * was not Retry's, if the page refused Retry's on a conflicted address — clears only a failure that
 * nothing has settled since.
 */
export type RegisterWriteOutcome = { ok: true } | { ok: false; reason?: string | null };

export interface RegisterSaveState {
  status: DraftSaveStatus;
  savedAt: Date | null;
  reason: string | null;
  /** Call before a write; pass the token to `settle`. */
  begin: () => number;
  /** Call after a write. `token` null settles a write that never began (it reports only its end). */
  settle: (token: number | null, outcome: RegisterWriteOutcome) => void;
  /** Re-read the register; never repeats a write. */
  retry: () => void;
}

/** The sentence when a failed write carried no reason of its own. */
export const REGISTER_SAVE_FALLBACK_REASON = 'The last change to the register could not be saved.';

type Read = 'no-twin' | 'loading' | 'error' | 'ready';

export const useRegisterSaveState = ({
  registerRead,
  reloadRegister,
  scope = null,
}: {
  registerRead: Read;
  reloadRegister: () => void;
  /** The twin the writes are for; a change drops everything (see the header). */
  scope?: string | number | null;
}): RegisterSaveState => {
  const nextToken = useRef(1);
  /** Tokens begun under the current scope; anything else is ignored at settle. */
  const scopeTokens = useRef<Set<number>>(new Set());
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());
  const [last, setLast] = useState<RegisterWriteOutcome | null>(null);
  /** Bumped on every settle, so Retry can tell whether a write settled after it was pressed. */
  const [settleSeq, setSettleSeq] = useState(0);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  /** The settle sequence Retry was pressed at, or null when no Retry is pending. */
  const [retryingAt, setRetryingAt] = useState<number | null>(null);

  const scopeRef = useRef(scope);
  useEffect(() => {
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;
    scopeTokens.current = new Set();
    setOpen(new Set());
    setLast(null);
    setSavedAt(null);
    setRetryingAt(null);
  }, [scope]);

  const begin = useCallback(() => {
    const token = nextToken.current++;
    scopeTokens.current.add(token);
    setOpen(prev => new Set(prev).add(token));
    return token;
  }, []);

  const settle = useCallback((token: number | null, outcome: RegisterWriteOutcome) => {
    if (token !== null) {
      // A write begun under another twin: its end is not this twin's news.
      if (!scopeTokens.current.has(token)) return;
      scopeTokens.current.delete(token);
      setOpen(prev => {
        if (!prev.has(token)) return prev;
        const next = new Set(prev);
        next.delete(token);
        return next;
      });
    }
    setLast(outcome);
    setSettleSeq(n => n + 1);
    if (outcome.ok) setSavedAt(new Date());
  }, []);

  const reloadRef = useRef(reloadRegister);
  reloadRef.current = reloadRegister;
  const settleSeqRef = useRef(settleSeq);
  settleSeqRef.current = settleSeq;
  const retry = useCallback(() => {
    setRetryingAt(settleSeqRef.current);
    reloadRef.current();
  }, []);

  /*
   * The re-read's answer. Watching the transition (not just `ready`) is what stops a read that was
   * already `ready` when Retry was pressed from clearing the failure before the new read started.
   */
  const previousRead = useRef(registerRead);
  useEffect(() => {
    const before = previousRead.current;
    previousRead.current = registerRead;
    if (retryingAt === null) return;
    if (registerRead === 'loading' || before !== 'loading') return;
    if (registerRead === 'ready' && settleSeq === retryingAt) setLast(null);
    setRetryingAt(null);
  }, [registerRead, retryingAt, settleSeq]);

  const failed = last !== null && !last.ok;
  const status: DraftSaveStatus =
    open.size > 0 || (retryingAt !== null && registerRead === 'loading')
      ? 'saving'
      : failed
        ? 'failed'
        : 'saved';
  const reason = last !== null && !last.ok ? (last.reason ?? REGISTER_SAVE_FALLBACK_REASON) : null;

  return { status, savedAt, reason, begin, settle, retry };
};
